import { NavLink, useLocation } from "react-router";
import { Icon } from "./icons";
import { SidebarGroup } from "./SidebarGroup";
import { usePolling } from "../lib/use-polling";
import { api } from "../lib/api";

// Workflow-first navigation (spec §3.1): the four things CS does, then Build,
// then the engineer surfaces under a collapsed Advanced group, then Help.
// Labels are sentence case in code; .sidebar__link uppercases them.

interface NavItem {
  to: string;
  label: string;
  icon: keyof typeof Icon;
  end?: boolean;
  count?: number;
}

/** One navigation link: icon, label, and a count when there is something waiting. */
function Item({ to, label, icon, end, count }: NavItem) {
  const I = Icon[icon];
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `sidebar__link${isActive ? " sidebar__link--active" : ""}`}
    >
      <span className="sidebar__icon"><I /></span>
      <span className="sidebar__label">{label}</span>
      {count ? <span className="sidebar__count" aria-label={`${count} waiting`}>{count}</span> : null}
    </NavLink>
  );
}

export const ADVANCED_PATHS: readonly string[] = ["/environments", "/queues", "/catalog", "/secrets"];

/** Stuck events waiting across every bus — the badge beside Needs attention. */
export function pendingCount(buses: Array<{ pending: number }> | undefined): number {
  return (buses ?? []).reduce((n, b) => n + (b.pending ?? 0), 0);
}

/**
 * Workflow-first navigation (spec §3.1): the four things CS does, then Build,
 * then the engineer surfaces under a collapsed Advanced group, then Help.
 *
 * Advanced is forced open while one of its routes is active, so nobody can be
 * standing on a page whose entry in the nav is hidden.
 */
export function Sidebar() {
  const { pathname } = useLocation();
  const summary = usePolling(() => api.dlqSummary(), 30000);
  const pending = pendingCount(summary.data?.buses);
  const inAdvanced = ADVANCED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  return (
    <nav className="sidebar" aria-label="Primary">
      <Item to="/clients" label="Clients" icon="Clients" />
      <Item to="/activity" label="Activity" icon="Runs" />
      <Item to="/attention" label="Needs attention" icon="DeadLetter" count={pending} />
      <Item to="/overview" label="Overview" icon="Overview" />
      <SidebarGroup id="build" label="Build">
        <Item to="/pipelines" label="Pipelines" icon="Pipelines" />
        <Item to="/connections" label="Connections" icon="Connections" />
      </SidebarGroup>
      <SidebarGroup id="advanced" label="Advanced" collapsible defaultOpen={false} forceOpen={inAdvanced}>
        <Item to="/environments" label="Environments" icon="Environments" />
        <Item to="/queues" label="Queues" icon="Connections" />
        <Item to="/catalog" label="Catalog" icon="Catalog" />
        <Item to="/secrets" label="Secrets" icon="Secrets" />
      </SidebarGroup>
      <SidebarGroup id="help" label="Help">
        <Item to="/docs" label="Docs" icon="Docs" />
        <Item to="/glossary" label="Glossary" icon="Glossary" />
      </SidebarGroup>
    </nav>
  );
}
